import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronDown, Loader2, Search, X } from 'lucide-react'
import { cn } from '@/lib/cn'

export interface AsyncOption {
  value: string
  label: string
}

interface Props {
  label?: string
  /** Valor selecionado (id). */
  value?: string
  /** Rótulo do valor selecionado (para exibir mesmo sem recarregar a lista). */
  selectedLabel?: string
  /** Busca server-side. Recebe o termo digitado e devolve as opções. */
  loadOptions: (query: string) => Promise<AsyncOption[]>
  onChange: (value: string, option: AsyncOption | null) => void
  placeholder?: string
  searchPlaceholder?: string
  disabled?: boolean
  required?: boolean
  clearable?: boolean
  wrapperClassName?: string
  className?: string
  /** Debounce da busca em ms. Default 300. */
  debounceMs?: number
}

/**
 * Select com busca assíncrona (server-side) — para listas grandes que não cabem
 * em memória (ex.: contrato principal entre milhares de credenciados).
 * Carrega sob demanda conforme o usuário digita; nunca baixa tudo de uma vez.
 */
export function AsyncSelect({
  label, value = '', selectedLabel, loadOptions, onChange,
  placeholder, searchPlaceholder = 'Digite para buscar', disabled, required,
  clearable = true, wrapperClassName, className, debounceMs = 300,
}: Props) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [options, setOptions] = useState<AsyncOption[]>([])
  const [loading, setLoading] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0, flipUp: false })
  // Lembra o rótulo da opção atualmente escolhida, mesmo que ela não esteja na lista carregada.
  const [picked, setPicked] = useState<AsyncOption | null>(
    value && selectedLabel ? { value, label: selectedLabel } : null,
  )

  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const searchInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (value && selectedLabel) setPicked({ value, label: selectedLabel })
    if (!value) setPicked(null)
  }, [value, selectedLabel])

  const displayLabel = useMemo(() => {
    if (!value) return null
    if (picked && picked.value === value) return picked.label
    return options.find(o => o.value === value)?.label ?? selectedLabel ?? null
  }, [value, picked, options, selectedLabel])

  // Busca debounced ao abrir e ao digitar.
  useEffect(() => {
    if (!open) return
    let active = true
    setLoading(true)
    const t = setTimeout(() => {
      loadOptions(query.trim())
        .then(opts => { if (active) setOptions(opts) })
        .catch(() => { if (active) setOptions([]) })
        .finally(() => { if (active) setLoading(false) })
    }, debounceMs)
    return () => { active = false; clearTimeout(t) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, query])

  const recalc = () => {
    const btn = triggerRef.current
    if (!btn) return
    const rect = btn.getBoundingClientRect()
    const panelH = panelRef.current?.offsetHeight ?? 320
    const flipUp = window.innerHeight - rect.bottom < panelH + 12 && rect.top > panelH + 12
    setPos({ top: flipUp ? rect.top - panelH - 4 : rect.bottom + 4, left: rect.left, width: rect.width, flipUp })
  }
  useLayoutEffect(() => {
    if (!open) return
    recalc()
    const id = requestAnimationFrame(recalc)
    setTimeout(() => searchInputRef.current?.focus(), 0)
    return () => cancelAnimationFrame(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open) return
    const onScroll = (e: Event) => {
      const t = e.target as Node | null
      if (panelRef.current && t && panelRef.current.contains(t)) return
      recalc()
    }
    const onResize = () => recalc()
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (triggerRef.current?.contains(t)) return
      if (panelRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  function pick(o: AsyncOption) {
    setPicked(o)
    onChange(o.value, o)
    setOpen(false)
    setQuery('')
  }
  function clear(e: React.MouseEvent) {
    e.stopPropagation()
    setPicked(null)
    onChange('', null)
  }

  const hasValue = value !== '' && value !== null && value !== undefined
  const showClear = clearable && hasValue && !disabled

  return (
    <div className={cn('flex flex-col', wrapperClassName)}>
      {label && (
        <label className="text-xs font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-rose-500 ml-0.5">*</span>}
        </label>
      )}

      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
        className={cn(
          'group relative w-full inline-flex items-center bg-white rounded-md ring-1 px-3 py-2 text-sm transition cursor-pointer',
          'disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400',
          open ? 'ring-brand-500 ring-2' : 'ring-gray-200 hover:ring-gray-300',
          className,
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={cn('break-words flex-1 text-left', displayLabel ? 'text-gray-900' : 'text-gray-400')}>
          {displayLabel ?? placeholder ?? 'Selecione'}
        </span>
        {showClear ? (
          <span
            role="button"
            tabIndex={-1}
            aria-label="Limpar"
            onClick={clear}
            className="cursor-pointer inline-flex items-center justify-center w-6 h-6 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition ml-1"
          >
            <X size={14} />
          </span>
        ) : (
          <ChevronDown size={14} className={cn('text-gray-400 transition-transform ml-1', open && 'rotate-180')} />
        )}
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, minWidth: 240, zIndex: 9999 }}
          className="bg-white ring-1 ring-gray-200 rounded-md py-1 max-h-[60vh] overflow-hidden flex flex-col"
        >
          <div className="px-2 py-2 border-b border-gray-100 sticky top-0 bg-white">
            <div className="relative flex items-center bg-gray-50 rounded ring-1 ring-gray-200 focus-within:ring-brand-500 focus-within:ring-2">
              <Search size={12} className="absolute left-2.5 text-gray-400 pointer-events-none" />
              <input
                ref={searchInputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                autoComplete="off"
                className="w-full bg-transparent text-xs pl-7 pr-7 py-1.5 focus:outline-none placeholder:text-gray-400"
              />
              {loading && <Loader2 size={12} className="absolute right-2 animate-spin text-gray-400" />}
            </div>
          </div>

          <div role="listbox" className="overflow-y-auto flex-1 max-h-[252px]">
            {loading && options.length === 0 ? (
              <div className="px-3 py-4 text-xs text-gray-400 text-center">Buscando</div>
            ) : options.length === 0 ? (
              <div className="px-3 py-4 text-xs text-gray-400 text-center">
                {query.trim() ? 'Nenhum resultado.' : 'Digite para buscar.'}
              </div>
            ) : options.map((o) => {
              const isSel = o.value === value
              return (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={isSel}
                  onClick={() => pick(o)}
                  className={cn(
                    'cursor-pointer w-full flex items-center justify-between gap-2 px-3 py-2 text-[13px] text-left transition',
                    isSel ? 'bg-brand-50 text-brand-700' : 'text-gray-700 hover:bg-gray-50',
                  )}
                >
                  <span className="break-words">{o.label}</span>
                  {isSel && <Check size={13} className="text-brand-600 shrink-0" />}
                </button>
              )
            })}
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}
